import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

const PATHWAYS = [
  {
    n: "01",
    title: "Direct Platform Capital",
    body:
      "Opportunities that meet the platform's underwriting and mandate criteria may be considered for direct platform capital formation through the partnership's vehicle architecture.",
    criteria: [
      "Asset-backed industrial businesses with tangible collateral, contracted utilisation or recurring revenue characteristics.",
      "Cheque size within mandate parameters (typically USD 15m – 250m+ depending on structure).",
      "Documented exit path or refinancing horizon (for credit and bridge structures).",
      "Governance and conflicts profile compatible with the partnership's posture.",
    ],
  },
  {
    n: "02",
    title: "Investor Access",
    body:
      "Selectively documented co-investment and single-asset SPV opportunities, advanced on a confidential, by-introduction basis to qualified institutional counterparties.",
    criteria: [
      "Single-asset or thematic co-investment vehicles alongside platform funds.",
      "Offered selectively to limited partners and strategic counterparties — not broadly marketed.",
      "Documented terms, reporting cadence and governance per investor.",
      "Reduced or no carry classes available by negotiation.",
    ],
  },
  {
    n: "03",
    title: "Fund-Structured Solutions",
    body:
      "Capital deployed through the platform's intended fund architecture, calibrated to the duration, governance and reporting preferences of institutional capital.",
    criteria: [
      "Commitments through one of the platform's nine intended vehicles (working titles in formation).",
      "Standard fund terms, subscription documents and institutional reporting cadence.",
      "Cayman Islands domicile via the CV5 SPC platform; governance via Bell Rock Group (engagements in formation).",
      "Independent administration, custody and audit counterparties.",
    ],
  },
  {
    n: "04",
    title: "Coordinated Introductions",
    body:
      "Where a transaction sits outside the platform's direct mandate parameters, the partnership may, where appropriate, coordinate introductions to selected external capital partners.",
    criteria: [
      "External network spans equity sponsors, private credit funds, real estate debt providers and strategic capital partners.",
      "Introductions are made on a documented, confidential basis — not broadly marketed.",
      "Kellermann Reid & Co. does not act as a placement agent or broker-dealer in any jurisdiction.",
      "No guarantee of funding, transaction outcome or third-party capital provider performance.",
    ],
  },
];

const STAGES = [
  {
    n: "i",
    title: "Mandate Fit Review",
    body:
      "Initial fit assessment against the platform's documented mandate criteria. Out-of-scope enquiries are declined or routed to the Coordinated Introductions pathway, with the partnership's discipline intact.",
  },
  {
    n: "ii",
    title: "Documented Diligence",
    body:
      "Where the fit is established, the partnership advances a documented diligence process — asset coverage, contracted revenue, governance review, conflicts mapping and Cayman platform onboarding.",
  },
  {
    n: "iii",
    title: "Investment Committee Decision",
    body:
      "All commitments are advanced through the partnership's single Investment Committee, with recorded deliberation and an independent risk vote. The IC does not delegate decisions to deal teams.",
  },
  {
    n: "iv",
    title: "Documentation & Funding",
    body:
      "Long-form documentation, intercreditor packages and subscription documents are executed prior to funding. Documentation is the underwriting, not the marketing.",
  },
];

const SUBJECT_TO = [
  "Diligence outcomes, including legal, tax and accounting review",
  "Suitability under applicable institutional investor categories",
  "Jurisdictional requirements, regulatory authorisations and KYC/AML completion",
  "Final approval by the relevant capital provider (for Coordinated Introductions)",
  "Vehicle formation, subscription documentation and IC ratification (for fund structures)",
];

const CapitalPathwaysPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.capitalPathwaysPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Capital Pathways"
          label="Mandate-Led · Selective · Compliance-Conscious"
          title={
            <>
              Selective{" "}
              <span className="italic text-[var(--kr-gold)]">
                Capital Pathways
              </span>
              .
            </>
          }
          description="Kellermann Reid & Co. is mandate-led. Opportunities that meet platform criteria may be considered for direct platform capital formation, investor access, or fund-structured solutions. Where a transaction sits outside direct mandate parameters, the partnership may coordinate introductions to selected external capital partners within its institutional network."
          breadcrumb="Capital Pathways"
        />

        {/* Lead institutional statement */}
        <section className="py-20 lg:py-24">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Posture
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15] mb-6">
                Mandate-led —{" "}
                <span className="italic text-[var(--kr-gold)]">not</span>{" "}
                volume-led.
              </h2>
              <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85]">
                The partnership engages selectively. Capital is committed where
                the platform&apos;s underwriting standards compound — and
                declined, or redirected, where they do not. Discipline is
                expressed at origination, not negotiated after.
              </p>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-6">
                Criteria &amp; Documented Norms
              </div>
              <ul
                data-testid={LANDING.capitalPathwaysPageLead}
                className="space-y-4"
              >
                {[
                  "The partnership is mandate-led — discipline is the underwriting, not the marketing.",
                  "Where a counterparty's transaction matches the platform's underwriting and mandate criteria, direct platform capital is the natural pathway.",
                  "Where the fit is partial, an alternative pathway — Investor Access or fund-structured solutions — may apply.",
                  "Where the transaction sits outside the partnership's direct discipline, introductions are coordinated with the external institutional network on documented terms — not stretched to fit.",
                  "Every pathway operates through the same documented engagement process, with the same subject-to conditions, and with the same disclosures.",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-4 text-[var(--kr-text)] text-[15.5px] leading-[1.85] border-b border-[var(--kr-line-soft)] pb-4"
                  >
                    <span
                      aria-hidden
                      className="mt-[9px] block w-[6px] h-[6px] flex-shrink-0"
                      style={{
                        background: "var(--kr-gold)",
                        transform: "rotate(45deg)",
                      }}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p
                data-testid={LANDING.capitalPathwaysPageFamilyOfficeLink}
                className="mt-8 text-[var(--kr-text-muted)] text-[14.5px] leading-[1.85] max-w-[66ch]"
              >
                For family offices and strategic capital partners seeking
                mandate-led opportunity screening and investment support,
                explore{" "}
                <Link
                  to="/family-office-investment-solutions"
                  className="text-[var(--kr-gold)] hover:text-[var(--kr-text)] transition-colors underline underline-offset-4 decoration-[var(--kr-gold)]/40"
                >
                  KR Family Office Investment Solutions →
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* 4 pathways — detailed */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-20">
            {PATHWAYS.map((p) => (
              <article
                key={p.n}
                data-testid={`${LANDING.capitalPathwaysPagePathwayPrefix}${p.n}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-[var(--kr-line-soft)] last:border-b-0"
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      Pathway {p.n}
                    </span>
                    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  </div>
                  <h3 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] mb-6">
                    {p.title}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85]">
                    {p.body}
                  </p>
                </div>
                <div className="lg:col-span-8">
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-6">
                    Criteria & Documented Norms
                  </div>
                  <ul className="space-y-4">
                    {p.criteria.map((c) => (
                      <li
                        key={c}
                        className="flex items-start gap-4 text-[var(--kr-text)] text-[14.5px] leading-[1.75] border-b border-[var(--kr-line-soft)] pb-3"
                      >
                        <span
                          aria-hidden
                          className="mt-[8px] block w-[5px] h-[5px] flex-shrink-0"
                          style={{
                            background: "var(--kr-gold)",
                            transform: "rotate(45deg)",
                          }}
                        />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Process / stages */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Engagement Process
                  </span>
                  <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[26ch]">
                  Four stages.{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Documented
                  </span>{" "}
                  throughout.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pl-8">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[58ch]">
                  Every engagement — across all four pathways — moves through
                  the same documented process. Confidence is necessary;
                  documentation is sufficient. The partnership commits where
                  the second exists.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {STAGES.map((s) => (
                <article
                  key={s.n}
                  data-testid={`${LANDING.capitalPathwaysPageStagePrefix}${s.n}`}
                  className="p-8 lg:p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div
                    className="w-12 h-12 border border-[var(--kr-gold)]/60 flex items-center justify-center font-serif-display text-[1.05rem] italic text-[var(--kr-gold)] mb-6 uppercase"
                    aria-hidden
                  >
                    {s.n}
                  </div>
                  <h3 className="font-serif-display text-[1.2rem] text-[var(--kr-text)] mb-4 leading-[1.25] group-hover:text-[var(--kr-gold)] transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.75]">
                    {s.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Subject to */}
        <section className="py-20 lg:py-24 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Subject To
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[24ch]">
                Every engagement,{" "}
                <span className="italic text-[var(--kr-gold)]">
                  written down
                </span>
                .
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <ul
                data-testid={LANDING.capitalPathwaysPageSubjectList}
                className="space-y-4 mb-8"
              >
                {SUBJECT_TO.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-4 text-[var(--kr-text)] text-[15px] leading-[1.85] border-b border-[var(--kr-line-soft)] pb-4"
                  >
                    <span
                      aria-hidden
                      className="mt-[8px] block w-[6px] h-[6px] flex-shrink-0"
                      style={{
                        background: "var(--kr-gold)",
                        transform: "rotate(45deg)",
                      }}
                    />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[13px] text-[var(--kr-text-muted)] italic leading-[1.75] max-w-[60ch]">
                Kellermann Reid &amp; Co. does not act as a placement agent or
                broker-dealer in any jurisdiction. The partnership does not
                guarantee funding, transaction outcomes, or the performance of
                any third-party capital provider. Introductions are made on a
                documented, confidential basis and do not constitute an offer
                or solicitation.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-24">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Initiate Dialogue
                </span>
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.4rem)] leading-[1.18] max-w-[34ch]">
                Capital relationships are advanced on a{" "}
                <span className="italic text-[var(--kr-gold)]">
                  documented, confidential
                </span>{" "}
                basis.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.capitalPathwaysPageRequest}
                className="kr-btn-primary"
              >
                Initiate Engagement
                <span aria-hidden>→</span>
              </button>
              <Link to="/disclosures" className="kr-btn-ghost">
                Read Full Disclosures
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default CapitalPathwaysPage;
